/* eslint-disable promise/always-return */
/* eslint-disable promise/catch-or-return */
/* eslint-disable no-console */
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box, CircularProgress, Typography } from '@mui/material';
import { SerializedError } from '@reduxjs/toolkit';
import { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { formatQueryError } from 'renderer/shared/formatters/formatters';
import { selectUserId } from 'renderer/features/auth/state/userSelectors';
import { useAppSelector } from 'renderer/shared/state/hooks';
import { isPurchaseConfirmation } from '../utils/applyCheckoutAuth';
import { buildPaypalReturnCaptureRequest } from '../utils/paypalReturnCapture';
import { useCreatePaypalCaptureMutation } from '../services/purchase.endpoints';
import PurchaseConfirmationPage from './PurchaseConfirmationPage';
import CheckoutNotice from './CheckoutNotice';

export default function PurchaseReturn() {
  const location = useLocation();
  const navigate = useNavigate();
  const [capturePaypalCheckout] = useCreatePaypalCaptureMutation();
  const currentUserId = useAppSelector(selectUserId);

  const [result, setResult] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [redirectingToPurchase, setRedirectingToPurchase] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const orderId = params.get('token'); // PayPal returns order token
    const cartId = params.get('cartId');

    if (!orderId) {
      setError('Missing PayPal order ID.');
      return;
    }

    const approval = JSON.parse(
      localStorage.getItem('paypalApproval') || 'null',
    );
    const capture = buildPaypalReturnCaptureRequest({
      orderId,
      cartId,
      approval,
      currentUserId,
    });
    if ('error' in capture) {
      console.error('⚠️ PayPal approval not found in storage');
      setError(capture.error);
      return;
    }

    setLoading(true);

    capturePaypalCheckout(capture.request)
      .unwrap()
      .then((data) => {
        console.log('✅ PayPal capture success:', data);
        localStorage.removeItem('paypalApproval');
        if (isPurchaseConfirmation(data)) {
          setResult(data);
        } else {
          setError(
            'Purchase completed but confirmation details are unavailable.',
          );
        }
      })
      .catch((err: FetchBaseQueryError | SerializedError) => {
        console.error('❌ PayPal capture failed:', err);
        localStorage.removeItem('paypalApproval');
        setRedirectingToPurchase(true);
        navigate('/purchase', {
          replace: true,
          state: {
            paypalCaptureError: formatQueryError(err),
            paymentProvider: 'paypal',
          },
        });
      })
      .finally(() => setLoading(false));
  }, [capturePaypalCheckout, currentUserId, location.search, navigate]);

  if (loading || redirectingToPurchase) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          minHeight: '50vh',
        }}
      >
        <CircularProgress size={100} />
        <Typography
          variant="body1"
          color="text.secondary"
          sx={{ mt: 2, fontStyle: 'italic' }}
        >
          Finalizing PayPal purchase…
        </Typography>
      </Box>
    );
  }

  if (error) {
    return <CheckoutNotice title="Checkout not completed" message={error} />;
  }

  return result ? <PurchaseConfirmationPage {...result} /> : null;
}
